import { getCartSuccessLabel } from '../shared/cart-feedback-label';
import { QUANTITY_STEPPER_CHANGE_EVENT, mountQuantitySteppers } from '../shared/quantity-stepper';
import { scrollToConfigurator } from '../shared/scroll-to-configurator';
import { initToggletipGroup } from '../shared/toggletip';

const ADD_TO_CART_TIMEOUT_MS = 15000;
const SUCCESS_RESET_MS = 1600;

export interface GroupedSelectionItem {
  sku: string;
  quantity: number;
}

interface AddToCartResult {
  requestId: string;
  success: boolean;
  error?: string;
}

export function collectGroupedSelection(panel: HTMLElement): GroupedSelectionItem[] {
  return Array.from(panel.querySelectorAll<HTMLElement>('[data-option-row]'))
    .map(row => {
      const sku = row.dataset.childSku?.trim();
      const input = row.querySelector<HTMLInputElement>('[data-quantity-value]');
      const quantity = Number.parseInt(input?.value ?? '', 10);

      if (!sku || !Number.isFinite(quantity) || quantity <= 0) {
        return null;
      }

      return { sku, quantity };
    })
    .filter((item): item is GroupedSelectionItem => item !== null);
}

export function mountProductOptionsPanel(panel: HTMLElement): void {
  if (panel.dataset.cartMounted === 'true') return;
  panel.dataset.cartMounted = 'true';

  mountQuantitySteppers(panel);

  const addButton = panel.querySelector<HTMLButtonElement>('[data-add-to-cart]');
  const buttonLabel = addButton?.querySelector<HTMLElement>('[data-button-label]');
  const feedback = panel.querySelector<HTMLElement>('[data-product-options-feedback]');
  const status = panel.querySelector<HTMLElement>('[data-product-options-status]');
  const productType = panel.dataset.productType;
  const defaultLabel = buttonLabel?.textContent?.trim() || '';

  let pendingRequestId: string | null = null;
  let loadTimeout: number | null = null;
  let successTimeout: number | null = null;

  const getItems = (): GroupedSelectionItem[] => {
    if (productType === 'grouped') return collectGroupedSelection(panel);
    return [];
  };

  const clearFeedback = (): void => {
    if (feedback) {
      feedback.hidden = true;
      feedback.textContent = '';
      feedback.removeAttribute('data-feedback-state');
    }
    if (buttonLabel) buttonLabel.textContent = defaultLabel;
    addButton?.removeAttribute('data-feedback-state');
  };

  const syncAddButton = (): void => {
    if (!addButton || pendingRequestId) return;
    addButton.disabled = getItems().length === 0;
  };

  const handleSelectionChange = (): void => {
    if (successTimeout !== null) {
      window.clearTimeout(successTimeout);
      successTimeout = null;
    }
    if (status) status.textContent = '';
    clearFeedback();
    syncAddButton();
  };

  panel.addEventListener(QUANTITY_STEPPER_CHANGE_EVENT, handleSelectionChange);
  panel.addEventListener('change', handleSelectionChange);

  addButton?.addEventListener('click', () => {
    const items = getItems();
    if (items.length === 0 || pendingRequestId) return;

    clearFeedback();
    addButton.disabled = true;
    addButton.dataset.feedbackState = 'loading';
    if (buttonLabel) buttonLabel.textContent = defaultLabel;

    const requestId = `${productType || 'product'}-${Date.now()}`;
    pendingRequestId = requestId;

    const onResult = (event: Event): void => {
      const result = (event as CustomEvent<AddToCartResult>).detail;
      if (result.requestId !== requestId) return;

      if (loadTimeout !== null) {
        window.clearTimeout(loadTimeout);
        loadTimeout = null;
      }
      document.removeEventListener('bentley:add-to-cart-result', onResult);
      pendingRequestId = null;

      if (result.success) {
        addButton.dataset.feedbackState = 'success';
        const successLabel = getCartSuccessLabel(panel, defaultLabel);
        if (buttonLabel) buttonLabel.textContent = successLabel;
        if (status) status.textContent = successLabel;
        successTimeout = window.setTimeout(() => {
          successTimeout = null;
          clearFeedback();
          if (status) status.textContent = '';
          syncAddButton();
        }, SUCCESS_RESET_MS);
        return;
      }

      clearFeedback();
      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent =
          result.error || 'Unable to add these options right now. Please try again.';
      }
      if (status) status.textContent = 'Unable to add these options to cart.';
      syncAddButton();
    };

    document.addEventListener('bentley:add-to-cart-result', onResult);
    loadTimeout = window.setTimeout(() => {
      document.removeEventListener('bentley:add-to-cart-result', onResult);
      pendingRequestId = null;
      handleSelectionChange();
      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent = 'Unable to add these options right now. Please try again.';
      }
      if (status) status.textContent = 'Unable to add these options to cart.';
    }, ADD_TO_CART_TIMEOUT_MS);

    document.dispatchEvent(
      new CustomEvent('bentley:add-to-cart-request', {
        detail: {
          requestId,
          planName:
            panel.dataset.planName?.trim() ||
            panel.querySelector('h3')?.textContent?.trim() ||
            'item',
          items
        }
      })
    );
  });

  syncAddButton();
}

export default function groupedPricingOptionsBlock(root: HTMLElement): void {
  initToggletipGroup(root, { idPrefix: 'pricing-options-note', describedBy: true });

  const panels = Array.from(root.querySelectorAll<HTMLElement>('[data-product-options-panel]'));
  panels.forEach(mountProductOptionsPanel);

  const configurator = root.querySelector<HTMLElement>('[data-pricing-configurator]');
  const previousButton = configurator?.querySelector<HTMLButtonElement>('[data-options-previous]');
  const inlineConfigureButton = root.querySelector<HTMLButtonElement>('[data-pricing-configure]');
  const summary = root.querySelector<HTMLElement>('[data-pricing-summary]');

  if (!configurator) return;

  previousButton?.addEventListener('click', () => {
    const dialog = configurator.closest<HTMLDialogElement>('dialog');
    if (dialog) {
      dialog.close();
      return;
    }

    configurator.hidden = true;
    if (summary) summary.hidden = false;
    inlineConfigureButton?.focus();
  });

  inlineConfigureButton?.addEventListener('click', () => {
    if (summary) summary.hidden = true;
    configurator.hidden = false;
    configurator.focus({ preventScroll: true });
    window.requestAnimationFrame(() => scrollToConfigurator(configurator));
  });
}
